import type CSVMapping from '~/types/CSVMapping';
import type CSVValue from '~/types/CSVValue';

import { ActionKeys } from '~/actions/constants';
import { type ActionTypes } from '~/actions/index';
import { type FieldMapping } from '~/types/CSVMapping';
import * as csvFields from '~/utils/csvFields';

export type CSVState = {
  readonly deleteTemplateModalIsOpen: boolean;
  readonly errorFields: readonly number[];
  readonly errorMessageShown: boolean;
  readonly errorModalIsOpen: boolean;
  readonly errorModalMessage: Maybe<string>;
  readonly errorModalTitle: Maybe<string>;
  readonly errorText: Maybe<string>;
  readonly fileUrl: Maybe<string>;
  readonly isLoading: boolean;
  readonly mapping: Maybe<Readonly<CSVMapping>>;
  readonly mappingHasChanged: boolean;
  readonly saveTemplateModalIsOpen: boolean;
  readonly templates: readonly CSVMapping[];
  readonly userValues: Maybe<readonly CSVValue[]>;
};

const initialState: CSVState = {
  deleteTemplateModalIsOpen: false,
  errorFields: [],
  errorMessageShown: false,
  errorModalIsOpen: false,
  errorModalMessage: null,
  errorModalTitle: null,
  errorText: null,
  fileUrl: null,
  isLoading: false,
  mapping: null,
  mappingHasChanged: false,
  saveTemplateModalIsOpen: false,
  templates: [],
  userValues: null,
};

const csvImportReducer = (state: CSVState = initialState, action: ActionTypes): CSVState => {
  switch (action.type) {
    case ActionKeys.CSV_IMPORT_FILE_UPLOAD:
      return {
        ...state,
        isLoading: true,
      };
    case ActionKeys.CSV_IMPORT_FILE_UPLOAD_SUCCESS:
      return {
        ...state,
        fileUrl: action.payload.fileUrl,
        isLoading: false,
        mapping: action.payload.mapping,
        templates: [action.payload.mapping],
        userValues: action.payload.userValues,
      };
    case ActionKeys.CSV_IMPORT_FILE_UPLOAD_ERROR:
      return {
        ...state,
        errorModalIsOpen: true,
        errorModalMessage: action.payload.errorMessage,
        errorModalTitle: action.payload.errorTitle,
        isLoading: false,
      };
    case ActionKeys.CSV_IMPORT_CHANGE_FIELD_MAPPING:
      let fieldMappingIndex = state.mapping!.fieldMappings.findIndex(
        (fieldMapping) => fieldMapping.colIdx === action.payload.index,
      );
      let newFieldMapping: Maybe<FieldMapping> = null;
      let startIndex: Maybe<number> = null;
      if (fieldMappingIndex === -1) {
        fieldMappingIndex = action.payload.index;
        newFieldMapping = {
          colIdx: action.payload.index,
          colName: action.payload.colName,
          field: action.payload.field,
        };
        startIndex = action.payload.index;
      } else {
        newFieldMapping = {
          ...state.mapping!.fieldMappings[fieldMappingIndex],
          field: action.payload.field,
        };
        startIndex = fieldMappingIndex + 1;
      }
      return {
        ...state,
        mapping: {
          ...state.mapping!,
          fieldMappings: [
            ...state.mapping!.fieldMappings.slice(0, fieldMappingIndex),
            newFieldMapping,
            ...state.mapping!.fieldMappings.slice(startIndex),
          ],
        },
        mappingHasChanged: true,
      };
    case ActionKeys.CSV_IMPORT_FETCH_TEMPLATES_SUCCESS:
      const newTemplates = action.payload.templates;
      return {
        ...state,
        templates: [state.mapping!, ...newTemplates],
      };
    case ActionKeys.CSV_IMPORT_FETCH_TEMPLATES_ERROR:
      return {
        ...state,
      };
    case ActionKeys.CSV_IMPORT_CHANGE_TEMPLATE:
      const newTemplate = state.templates.find((template) => template.id === action.value);
      const colIndexes = newTemplate!.fieldMappings.reduce<Record<number, FieldMapping>>(
        (hash, fieldMapping) => ({ ...hash, [fieldMapping.colIdx]: fieldMapping }),
        {},
      );
      const fieldMappings: FieldMapping[] = [];
      let index = 0;
      for (const userValue of state.userValues!) {
        fieldMappings[index] = !colIndexes[index]
          ? {
              colIdx: index,
              colName: userValue.colName,
              field: csvFields.ignoreField,
            }
          : {
              colIdx: index,
              colName: userValue.colName,
              field: colIndexes[index].field,
            };
        index += 1;
      }
      return {
        ...state,
        mapping: {
          ...newTemplate!,
          fieldMappings,
        },
        mappingHasChanged: false,
      };
    case ActionKeys.CSV_IMPORT_SAVE_TEMPLATE_SUCCESS:
      return {
        ...state,
        saveTemplateModalIsOpen: false,
      };
    case ActionKeys.CSV_IMPORT_SAVE_TEMPLATE_ERROR:
      return {
        ...state,
        errorModalIsOpen: true,
        errorModalMessage: action.payload.errorMessage,
        errorModalTitle: action.payload.errorTitle,
      };
    case ActionKeys.CSV_IMPORT_DELETE_TEMPLATE:
      return {
        ...state,
        isLoading: true,
      };
    case ActionKeys.CSV_IMPORT_DELETE_TEMPLATE_MODAL_OPEN:
      return {
        ...state,
        deleteTemplateModalIsOpen: !state.deleteTemplateModalIsOpen,
      };
    case ActionKeys.CSV_IMPORT_DELETE_TEMPLATE_SUCCESS:
      const fetchedTemplates = action.payload.templates;
      const defaultMapping = state.templates.find((template) => template.name === 'Default');
      if (
        defaultMapping &&
        state.mapping &&
        action.payload.deletedTemplateId === state.mapping.id
      ) {
        return {
          ...state,
          deleteTemplateModalIsOpen: false,
          isLoading: false,
          mapping: defaultMapping,
          templates: [defaultMapping, ...fetchedTemplates],
        };
      }

      return {
        ...state,
        deleteTemplateModalIsOpen: false,
        isLoading: false,
        templates: [defaultMapping!, ...fetchedTemplates],
      };
    case ActionKeys.CSV_IMPORT_DELETE_TEMPLATE_ERROR:
      return {
        ...state,
        errorModalMessage: action.payload.errorMessage,
        errorModalTitle: action.payload.errorTitle,
        isLoading: false,
      };
    case ActionKeys.CSV_IMPORT_OPEN_SAVE_CSV_TEMPLATE_MODAL:
      return {
        ...state,
        errorFields: [],
        errorMessageShown: false,
        errorText: null,
        saveTemplateModalIsOpen: true,
      };
    case ActionKeys.CSV_IMPORT_CLOSE_SAVE_CSV_TEMPLATE_MODAL:
      return {
        ...state,
        saveTemplateModalIsOpen: false,
      };
    case ActionKeys.CSV_IMPORT_OPEN_ERROR_MESSAGE:
      return {
        ...state,
        errorFields: action.payload.fields,
        errorMessageShown: true,
        errorText: action.payload.text,
      };
    case ActionKeys.CSV_IMPORT_ORDERS_SUCCESS:
      return {
        ...state,
        saveTemplateModalIsOpen: false,
      };
    case ActionKeys.CSV_IMPORT_ORDERS_ERROR:
      return {
        ...state,
        errorModalIsOpen: true,
        errorModalMessage: action.payload.errorMessage,
        errorModalTitle: action.payload.errorTitle,
      };
    case ActionKeys.CSV_CLEAN_STATE:
      return {
        ...initialState,
      };
    default:
      return state;
  }
};

export default csvImportReducer;
